<template>
    <div
        class="atr-row"
        :style="{ left: leftPct + '%', width: widthPct + '%' }"
        :title="label"
        @mousedown="onBodyMouseDown"
    >
        <div class="atr-handle atr-handle-left" @mousedown.stop="e => onEdgeMouseDown('start', e)"></div>
        <i class="fa-solid fa-music"></i>
        <span class="atr-label">{{ label }}</span>
        <i class="fa-solid fa-trash atr-remove" @mousedown.stop @click.stop="$emit('remove')"></i>
        <div class="atr-handle atr-handle-right" @mousedown.stop="e => onEdgeMouseDown('end', e)"></div>
    </div>
</template>

<script>
// Same drag/resize pattern as OverlayTrackRow.vue (move the whole block, or trim either edge),
// adapted for an audio clip's own field names (timelineStartMs/timelineEndMs, not startMs/endMs) -
// background music tracks can freely overlap each other, so unlike overlays there's no packing
// into shared rows to worry about, each clip always gets its own lane.
const MIN_DURATION_MS = 200

export default {
    name: 'AudioTrackRow',

    props: {
        clip: {
            type: Object,
            required: true
        },
        totalDurationMs: {
            type: Number,
            required: true
        },
        label: {
            type: String,
            default: ''
        }
    },

    computed: {
        leftPct() {
            return (this.clip.timelineStartMs / this.totalDurationMs) * 100
        },
        widthPct() {
            return Math.max(2, ((this.clip.timelineEndMs - this.clip.timelineStartMs) / this.totalDurationMs) * 100)
        }
    },

    methods: {
        onBodyMouseDown(e) {
            this.$emit('select')
            const ruler = e.currentTarget.closest('.at-tracks-ruler')
            if (!ruler) return

            const rulerWidth = ruler.getBoundingClientRect().width
            const msPerPx = this.totalDurationMs / rulerWidth
            const startX = e.clientX
            const originalStart = this.clip.timelineStartMs
            const duration = this.clip.timelineEndMs - this.clip.timelineStartMs

            const onMove = (moveEvent) => {
                const deltaMs = (moveEvent.clientX - startX) * msPerPx
                const newStart = Math.min(Math.max(0, originalStart + deltaMs), this.totalDurationMs - duration)
                this.$emit('update', { timelineStartMs: Math.round(newStart), timelineEndMs: Math.round(newStart + duration) })
            }
            const onUp = () => {
                document.removeEventListener('mousemove', onMove)
                document.removeEventListener('mouseup', onUp)
            }
            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
        },

        onEdgeMouseDown(edge, e) {
            this.$emit('select')
            const ruler = e.target.closest('.at-tracks-ruler')
            if (!ruler) return

            const rulerWidth = ruler.getBoundingClientRect().width
            const msPerPx = this.totalDurationMs / rulerWidth
            const startX = e.clientX
            const originalStart = this.clip.timelineStartMs
            const originalEnd = this.clip.timelineEndMs

            const onMove = (moveEvent) => {
                const deltaMs = (moveEvent.clientX - startX) * msPerPx
                if (edge === 'start') {
                    const newStart = Math.min(Math.max(0, originalStart + deltaMs), originalEnd - MIN_DURATION_MS)
                    this.$emit('update', { timelineStartMs: Math.round(newStart), timelineEndMs: originalEnd })
                } else {
                    const newEnd = Math.max(Math.min(this.totalDurationMs, originalEnd + deltaMs), originalStart + MIN_DURATION_MS)
                    this.$emit('update', { timelineStartMs: originalStart, timelineEndMs: Math.round(newEnd) })
                }
            }
            const onUp = () => {
                document.removeEventListener('mousemove', onMove)
                document.removeEventListener('mouseup', onUp)
            }
            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
        }
    }
}
</script>

<style lang="scss" scoped></style>
